import Autosuggest, {
  SuggestionsFetchRequestedParams,
  ChangeEvent,
  InputProps,
  SuggestionSelectedEventData,
  RenderSuggestionParams,
} from 'react-autosuggest';
import React, { useState, FormEvent, useEffect } from 'react';
import { ReactComponent as EditIcon } from 'components/icons/editIcon.svg';
import { observer } from 'mobx-react-lite';
import { ITeam } from 'interfaces/team/ITeam';
import { ServiceTypes } from 'constants/ServiceTypes';
import { TeamStore } from 'stores/TeamStore';
import { useInjection } from 'services/ioc/ioc.react';
import { SelectedTeam, TeamSelectorWrapper } from './TeamSelectorStyles';

export interface IProps {
  placeholder: string;
  onSelectionChange: (selectedTeam?: ITeam) => any;
  selectedTeam?: ITeam;
  unavailableTeamId?: string;
  isReadOnly: boolean;
}
export const TeamSelector: React.FC<IProps> = observer((props: IProps) => {
  const teamStore = useInjection<TeamStore>(ServiceTypes.TeamStore);
  const [suggestions, setSuggestions] = useState<ITeam[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const { placeholder, onSelectionChange, selectedTeam, unavailableTeamId, isReadOnly } = props;

  useEffect(() => {
    teamStore.initializeIndex();
  }, []);

  const onSuggestionsFetchRequested = (params: SuggestionsFetchRequestedParams) => {
    setSuggestions(getSuggestions(params.value, unavailableTeamId));
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const getSuggestions = (value: string, filterIds?: string) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;

    return inputLength < 3 ? [] : teamStore.searchTeamsByName(inputValue, filterIds);
  };

  const getSuggestionValue = (suggestion: ITeam) => suggestion.name;

  const renderSuggestion = (suggestion: ITeam, params: RenderSuggestionParams) => {
    const tokens = params.query.split(' ').map((x) => x.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'));
    let { name } = suggestion;
    tokens.forEach((token) => {
      const reg = new RegExp(`\\b(${token})`, 'gi');
      name = name.replace(reg, `<mark>$1</mark>`);
    });

    return (
      <div>
        <p dangerouslySetInnerHTML={{ __html: name }} />
      </div>
    );
  };

  const onChange = (event: FormEvent<any>, params: ChangeEvent) => {
    setInputValue(params.newValue);
  };

  const onSuggestionSelected = (event: FormEvent<any>, data: SuggestionSelectedEventData<ITeam>) => {
    onSelectionChange(data.suggestion);
  };

  const onSelectionCleared = () => {
    onSelectionChange(undefined);
    setInputValue('');
  };

  const renderInputComponent = (props: any) => (
    <div>
      <label className='autocomplete-input-label'>
        <input {...props} />
      </label>
    </div>
  );
  const inputProps = {
    placeholder: placeholder,
    value: inputValue,
    onChange,
    'data-test': 'team-selector-input',
  } as InputProps<ITeam>;

  return (
    <TeamSelectorWrapper>
      {selectedTeam ? (
        <SelectedTeam className='selected-team'>
          <span>{selectedTeam.name}</span>
          {!isReadOnly ? <EditIcon data-testid={`remove-selected-btn`} cursor='pointer' onClick={onSelectionCleared} /> : null}
        </SelectedTeam>
      ) : (
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          inputProps={inputProps}
          onSuggestionSelected={onSuggestionSelected}
          renderInputComponent={renderInputComponent}
        />
      )}
    </TeamSelectorWrapper>
  );
});
